<template>
    <div class="form-nav vf-col-12 d-flex justify-content-between align-items-center">
        <button v-if="showBack" type="button" class="btn btn-ghost" @click="goBack">
            <em class="far fa-chevron-left mR-5"></em> Back
        </button>
        <span v-else></span>
        <div class="d-flex align-items-center" style="gap: 30px;">
            <button v-if="showAgreeAll" type="button" class="btn btn-outline-primary" @click="agreeAll">
                Agree All
            </button>
            <button v-if="showNext && !showPurchase" type="button" class="btn btn-primary" @click="goNext">
                {{ nextText }} <em class="far fa-chevron-right mL-5"></em>
            </button>
            <button v-if="showPurchase" type="button" class="btn btn-primary" @click="handlePurchase">
                <em class="far fa-shopping-cart mR-5"></em> Purchase
            </button>
        </div>
    </div>
</template>

<script>
import { defineElement } from '@vueform/vueform'
import { inject } from 'vue'
import { markAddressSubmitted } from '@/composables/addressValidationSignal'
import { useQuoteErrors } from '@/composables/useQuoteErrors'

export default defineElement({
    name: "FormNavElement",

    props: {
        showBack: {
            type: Boolean,
            default: true,
        },
        showNext: {
            type: Boolean,
            default: true,
        },
        showPurchase: {
            type: Boolean,
            default: false,
        },
        showAgreeAll: {
            type: Boolean,
            default: false,
        },
        nextText: {
            type: String,
            default: 'Next',
        },
    },

    setup(props, { element }) {
        const vueformApi = inject('vueformApi', null)
        const quoteErrors = useQuoteErrors()

        const goBack = () => {
            const form$ = vueformApi?.value
            quoteErrors.clear()
            if (form$) form$.$emit('back')
        }

        const goNext = async () => {
            const form$ = vueformApi?.value
            if (!form$) return
            // A submit attempt reveals the premises-address error (if any); it's hidden again the
            // moment the user re-engages the address field (see addressValidationSignal).
            markAddressSubmitted()
            form$.$el?.classList?.add('is-validated')
            await form$.validate()
            if (form$.invalid) {
                // Show client-side validation errors at the top of the step (banner scrolls itself into view).
                quoteErrors.setClient(Array.isArray(form$.errors) ? form$.errors : [])
                return
            }
            quoteErrors.clear()
            form$.submit()
        }

        const handlePurchase = async () => {
            const form$ = vueformApi?.value

            // No Vueform runtime available - fall back to the legacy direct submit.
            if (!form$) {
                document.getElementById('qform')?.submit();
                return
            }

            //Validates the form and submits if valid, otherwise does nothing
            form$.$el?.classList?.add('is-validated')
            await form$.validate()
            if (form$.invalid) return

            document.getElementById('qform')?.submit();
        }

        // Resolve an element's configured `items` array from the form schema using
        // its dotted path (e.g. "Assumptions.show_state"). The schema nests child
        // definitions under a `schema` key at every object level, so we walk
        // segment-by-segment: schema[seg0].schema[seg1]... .items
        const getSchemaItems = (form$, path) => {
            if (!form$?.schema || !path) return null
            const segments = path.split('.')
            let node = form$.schema
            for (let i = 0; i < segments.length; i++) {
                node = node?.[segments[i]]
                if (!node) return null
                if (i < segments.length - 1) node = node?.schema
            }
            return Array.isArray(node?.items) ? node.items : null
        }

        // For an agree/disagree question, find the option whose label is "Agree".
        // The value differs per question set (agreedisagree => true,
        // agreedisagreeNegatives => false), so we match on label, not value.
        const findAgreeOption = (items) =>
            items.find((item) => String(item?.label ?? '').trim().toLowerCase() === 'agree')

        // Recursively walk an element, setting every currently-visible
        // agree/disagree question to its "Agree" value. Objects/groups are
        // recursed into; anything not currently available/visible (i.e. hidden by
        // conditions) is skipped so we only touch what the user can actually see.
        const agreeVisibleQuestions = (form$, el$) => {
            if (!el$) return
            if (el$.available === false || el$.visible === false) return

            const children = el$.children$
            if (children) {
                Object.values(children).forEach((child$) => agreeVisibleQuestions(form$, child$))
                return
            }

            const items = getSchemaItems(form$, el$.path)
            if (!items) return

            const agreeOption = findAgreeOption(items)
            if (!agreeOption) return

            try {
                el$.update(agreeOption.value)
            } catch {
                el$.value = agreeOption.value
            }
        }

        const agreeAll = () => {
            const form$ = vueformApi?.value
            if (!form$?.schema) return
            Object.keys(form$.schema).forEach((key) => {
                agreeVisibleQuestions(form$, form$.el$(key))
            })
        }

        return { goBack, goNext, handlePurchase, agreeAll }
    }
});
</script>
